import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import {
  Users, MessageSquare, Pencil, Highlighter, Bookmark, Tag,
  Copy, Check, Send, X, MoreHorizontal, Settings, Loader2, Plus, Trash2,
  UserPlus, Share2, Link2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  listCollabSessions,
  getCollabSession,
  createCollabSession,
  updateCollabSession,
  deleteCollabSession,
  joinCollabSession,
  addCollabAnnotation,
  resolveCollabAnnotation,
  addCollabComment,
  type CollabSession,
  type CollabAnnotation,
  type CollabComment,
  type CollabParticipant,
} from '../services/collab-session-service';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { PageLayout } from '@/components/layout/PageLayout';
import { useAuthStore } from '@/stores/authStore';
import { CollabDocumentPanel } from '@/components/collab/CollabDocumentPanel';
import {
  ANNOTATION_COLORS,
  type AnnotationType,
  type CollabTextSelection,
  getAnnotationNote,
  getAnnotationPosition,
  getSelectedQuote,
  type CollabSharedDocument,
} from '@/components/collab/collabTypes';

const SESSION_STORAGE_KEY = 'khiimori_collab_session_id';

function isNotFoundError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 404;
}

function displayNameFromUser(user: { full_name?: string; name?: string; username?: string; email?: string } | null): string {
  if (!user) return 'You';
  return user.full_name || user.name || user.username || user.email?.split('@')[0] || 'You';
}

function normalizeSession(
  raw: CollabSession,
  userId: string,
  userName: string,
): CollabSession {
  const participants: CollabParticipant[] =
    raw.participants?.length > 0
      ? raw.participants
      : [{
          id: raw.ownerId,
          displayName: userName,
          activity: 'idle',
          isActive: true,
          role: 'owner',
        }];

  return {
    ...raw,
    participants: participants.map((p) =>
      p.id === userId || p.id === raw.ownerId
        ? { ...p, displayName: p.role === 'owner' ? userName : p.displayName }
        : p,
    ),
    annotations: raw.annotations ?? [],
    comments: raw.comments ?? [],
  };
}

function formatCommentTime(ts: string): string {
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return ts;
  }
}

export default function CollaborativeSessionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const userId = user?.id ?? '1';
  const userName = displayNameFromUser(user);

  const [session, setSession] = useState<CollabSession | null>(null);
  const [annotations, setAnnotations] = useState<CollabAnnotation[]>([]);
  const [comments, setComments] = useState<CollabComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [activeAnnotation, setActiveAnnotation] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showParticipants, setShowParticipants] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [inviteCodeInput, setInviteCodeInput] = useState('');
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinModalTab, setJoinModalTab] = useState<'share' | 'join'>('share');
  const [copiedLink, setCopiedLink] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsName, setSettingsName] = useState('');
  const [settingsMaxParticipants, setSettingsMaxParticipants] = useState(10);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [textSelection, setTextSelection] = useState<CollabTextSelection | null>(null);
  const [pendingAnnotation, setPendingAnnotation] = useState<{
    type: AnnotationType;
    selection: CollabTextSelection;
    tabId?: string;
    tabTitle?: string;
    tabUrl?: string;
  } | null>(null);
  const [annotationNote, setAnnotationNote] = useState('');
  const [isSavingAnnotation, setIsSavingAnnotation] = useState(false);
  const [sharedDocument, setSharedDocument] = useState<CollabSharedDocument | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);

  const applySession = useCallback(
    (raw: CollabSession) => {
      const normalized = normalizeSession(raw, userId, userName);
      setSession(normalized);
      setAnnotations(normalized.annotations ?? []);
      setComments(normalized.comments ?? []);
      localStorage.setItem(SESSION_STORAGE_KEY, normalized.id);
    },
    [userId, userName],
  );

  const refreshSession = useCallback(
    async (sessionId: string) => {
      const fresh = await getCollabSession(sessionId);
      applySession(fresh);
    },
    [applySession],
  );

  const fetchSessionData = useCallback(async (options?: { silent?: boolean }): Promise<CollabSession | null> => {
    try {
      if (!options?.silent) {
        setIsLoading(true);
      }
      setError(null);

      const savedId = localStorage.getItem(SESSION_STORAGE_KEY);
      if (savedId) {
        try {
          const saved = await getCollabSession(savedId);
          applySession(saved);
          return saved;
        } catch {
          localStorage.removeItem(SESSION_STORAGE_KEY);
        }
      }

      const sessions = await listCollabSessions();
      if (sessions.length > 0) {
        applySession(sessions[0]);
        return sessions[0];
      }

      const created = await createCollabSession({
        name: 'New Collaborative Session',
        maxParticipants: 10,
      });
      applySession(created);
      return created;
    } catch (err) {
      console.error('Failed to fetch session data:', err);
      setError('Failed to load session. Please try again.');
      setSession(null);
      setAnnotations([]);
      setComments([]);
      return null;
    } finally {
      if (!options?.silent) {
        setIsLoading(false);
      }
    }
  }, [applySession]);

  const reconcileSession = useCallback(async (): Promise<CollabSession | null> => {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setError('Session expired after a server restart. Restoring your session...');
    const fresh = await fetchSessionData({ silent: true });
    if (fresh) {
      setError(null);
    }
    return fresh;
  }, [fetchSessionData]);

  useEffect(() => {
    fetchSessionData();
  }, [fetchSessionData]);

  useEffect(() => {
    const code = searchParams.get('code')?.trim();
    if (!code || isLoading) return;
    setInviteCodeInput(code);
    setJoinModalTab('join');
    setShowJoinModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('code');
    setSearchParams(next, { replace: true });
  }, [isLoading, searchParams, setSearchParams]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !session?.id) return;
      getCollabSession(session.id).catch(() => {
        reconcileSession();
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [session?.id, reconcileSession]);

  useEffect(() => {
    if (!session?.id) return;
    const interval = window.setInterval(() => {
      refreshSession(session.id).catch(() => undefined);
    }, 8000);
    return () => window.clearInterval(interval);
  }, [session?.id, refreshSession]);

  const handleCopyInvite = () => {
    if (session?.inviteCode) {
      navigator.clipboard.writeText(session.inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const buildInviteLink = (inviteCode: string) =>
    `${window.location.origin}/sync?section=collab&code=${encodeURIComponent(inviteCode)}`;

  const handleCopyInviteLink = () => {
    if (!session?.inviteCode) return;
    navigator.clipboard.writeText(buildInviteLink(session.inviteCode));
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleJoinSession = async () => {
    const code = inviteCodeInput.trim();
    if (!code) return;
    try {
      setIsJoining(true);
      setError(null);
      const joined = await joinCollabSession(code, userName);
      applySession(joined);
      setInviteCodeInput('');
      setShowJoinModal(false);
    } catch (err) {
      console.error('Failed to join session:', err);
      setError('Failed to join session. Please check the invite code.');
    } finally {
      setIsJoining(false);
    }
  };

  const openShareModal = () => {
    setError(null);
    setJoinModalTab('share');
    setShowJoinModal(true);
  };

  const openJoinModal = () => {
    setError(null);
    setJoinModalTab('join');
    setShowJoinModal(true);
  };

  const handleCreateNewSession = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const created = await createCollabSession({
        name: 'New Collaborative Session',
        maxParticipants: 10,
      });
      applySession(created);
    } catch (err) {
      console.error('Failed to create session:', err);
      setError('Failed to create session.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendComment = async () => {
    if (!newComment.trim() || !session) return;
    const content = newComment.trim();
    setNewComment('');

    try {
      const saved = await addCollabComment(session.id, {
        content,
        userId,
        displayName: userName,
      });
      setComments((prev) => [...prev, saved]);
      setError(null);
    } catch (err) {
      if (isNotFoundError(err)) {
        const fresh = await reconcileSession();
        if (fresh) {
          try {
            const saved = await addCollabComment(fresh.id, { content, userId, displayName: userName });
            setComments((prev) => [...prev, saved]);
            setError(null);
            return;
          } catch (retryErr) {
            console.error('Failed to add comment after reconcile:', retryErr);
          }
        }
      }
      console.error('Failed to add comment:', err);
      setComments((prev) => [
        ...prev,
        {
          id: `local-${Date.now()}`,
          userId,
          displayName: userName,
          content,
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  };

  const saveAnnotation = async (
    type: AnnotationType,
    selection: CollabTextSelection,
    note: string,
    docMeta?: { tabId?: string; tabTitle?: string; tabUrl?: string },
  ) => {
    let activeSession = session;
    if (!activeSession) return false;

    const payload = {
      type,
      userId,
      displayName: userName,
      content: note.trim() || selection.selectedText,
      color: ANNOTATION_COLORS[type],
      position: {
        start: selection.start,
        end: selection.end,
        selectedText: selection.selectedText,
        tabId: docMeta?.tabId,
        tabTitle: docMeta?.tabTitle,
        tabUrl: docMeta?.tabUrl,
      },
      isResolved: false,
    };

    try {
      const saved = await addCollabAnnotation(activeSession.id, payload);
      setAnnotations((prev) => [...prev, saved]);
      setError(null);
      window.getSelection()?.removeAllRanges();
      setTextSelection(null);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} saved — visible to all participants`);
      return true;
    } catch (err) {
      if (isNotFoundError(err)) {
        const fresh = await reconcileSession();
        if (fresh) {
          try {
            const saved = await addCollabAnnotation(fresh.id, payload);
            setAnnotations((prev) => [...prev, saved]);
            setError(null);
            window.getSelection()?.removeAllRanges();
            setTextSelection(null);
            toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} saved`);
            return true;
          } catch (retryErr) {
            console.error('Failed to add annotation after reconcile:', retryErr);
          }
        }
      }
      console.error('Failed to add annotation:', err);
      setError('Failed to save annotation. Try refreshing the page.');
      toast.error('Could not save annotation');
      return false;
    }
  };

  const beginAnnotation = (type: AnnotationType) => {
    if (!textSelection) {
      toast.message('Select text in the page first', {
        description: 'Highlight the passage you want to annotate, then choose an action.',
      });
      return;
    }
    if (type === 'highlight') {
      void saveAnnotation(type, textSelection, textSelection.selectedText, {
        tabId: sharedDocument?.tabId,
        tabTitle: sharedDocument?.title,
        tabUrl: sharedDocument?.url,
      });
      return;
    }
    setPendingAnnotation({
      type,
      selection: textSelection,
      tabId: sharedDocument?.tabId,
      tabTitle: sharedDocument?.title,
      tabUrl: sharedDocument?.url,
    });
    setAnnotationNote(
      type === 'comment' ? '' : type === 'tag' ? '' : `Bookmark: ${textSelection.selectedText.slice(0, 80)}`,
    );
  };

  const handleConfirmAnnotation = async () => {
    if (!pendingAnnotation) return;
    setIsSavingAnnotation(true);
    const note =
      pendingAnnotation.type === 'tag' && annotationNote.trim()
        ? `Tag: ${annotationNote.trim()}`
        : annotationNote.trim() || pendingAnnotation.selection.selectedText;
    const ok = await saveAnnotation(
      pendingAnnotation.type,
      pendingAnnotation.selection,
      note,
      {
        tabId: pendingAnnotation.tabId,
        tabTitle: pendingAnnotation.tabTitle,
        tabUrl: pendingAnnotation.tabUrl,
      },
    );
    setIsSavingAnnotation(false);
    if (ok) {
      setPendingAnnotation(null);
      setAnnotationNote('');
    }
  };

  const handleAddAnnotation = (type: AnnotationType) => {
    beginAnnotation(type);
  };

  const handleResolveAnnotation = async (id: string) => {
    if (!session) return;
    setAnnotations((prev) => prev.map((a) => (a.id === id ? { ...a, isResolved: true } : a)));
    try {
      await resolveCollabAnnotation(session.id, id);
    } catch (err) {
      console.error('Failed to resolve annotation:', err);
      await refreshSession(session.id);
    }
  };

  const openSettings = () => {
    if (!session) return;
    setSettingsName(session.name);
    setSettingsMaxParticipants(session.maxParticipants);
    setShowSettings(true);
  };

  const handleSaveSettings = async () => {
    if (!session) return;
    try {
      setIsSavingSettings(true);
      const updated = await updateCollabSession(session.id, {
        name: settingsName.trim() || session.name,
        maxParticipants: settingsMaxParticipants,
      });
      applySession({ ...session, ...updated });
      setShowSettings(false);
    } catch (err) {
      console.error('Failed to update session:', err);
      setError('Failed to save settings.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleDeleteSession = async () => {
    if (!session) return;
    try {
      await deleteCollabSession(session.id);
      localStorage.removeItem(SESSION_STORAGE_KEY);
      setShowSettings(false);
      setSession(null);
      await fetchSessionData();
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError('Failed to delete session.');
    }
  };

  const getAnnotationIcon = (type: string) => {
    switch (type) {
      case 'highlight': return <Highlighter className="w-4 h-4" />;
      case 'comment': return <MessageSquare className="w-4 h-4" />;
      case 'drawing': return <Pencil className="w-4 h-4" />;
      case 'bookmark': return <Bookmark className="w-4 h-4" />;
      case 'tag': return <Tag className="w-4 h-4" />;
      default: return <Bookmark className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex items-center gap-3 text-success-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading collaborative session...</span>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center py-16 px-6">
        <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl rounded-2xl p-8 border border-khiimori-border/10 max-w-md w-full">
          <h2 className="text-2xl font-bold text-khiimori-text-primary mb-4">Join a Session</h2>
          <p className="text-khiimori-text-muted mb-6">
            Enter an invite code to join an existing collaborative session.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-error-500/20 border border-error-500/30 rounded-lg text-error-400 text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <Input
              value={inviteCodeInput}
              onChange={(e) => setInviteCodeInput(e.target.value)}
              placeholder="Enter invite code"
            />
            <Button
              onClick={handleJoinSession}
              disabled={!inviteCodeInput.trim() || isJoining}
              variant="primary"
              isLoading={isJoining}
            >
              Join
            </Button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-khiimori-text-muted text-sm">Or</p>
            <Button onClick={handleCreateNewSession} variant="glass" className="mt-3">
              Create New Session
            </Button>
            <Button onClick={openJoinModal} variant="ghost" className="mt-2 w-full">
              Have an invite code?
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const activeAnnotations = annotations.filter((a) => !a.isResolved);

  return (
    <PageLayout
      title="Collaborative Session"
      description="Real-time collaborative tab sessions with annotations and chat"
      icon={Users}
      embedded
    >
      <div className="flex flex-col lg:flex-row min-h-[640px] rounded-xl border border-khiimori-border/10 overflow-hidden bg-khiimori-bg-secondary/20">
        {/* Main column */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border-b border-khiimori-border/10 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-4 min-w-0">
                <div className="p-3 bg-gradient-to-br from-success-500 to-info-500 rounded-xl shadow-lg shadow-success-500/20 shrink-0">
                  <Users className="w-6 h-6 text-khiimori-text-primary" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl font-bold text-khiimori-text-primary truncate">{session.name}</h1>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${session.status === 'active' ? 'bg-success-500 animate-pulse' : 'bg-khiimori-bg-tertiary'}`} />
                    <span className="text-khiimori-text-muted text-sm">
                      {session.participants.length} participant{session.participants.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  onClick={openJoinModal}
                  variant="glass"
                  size="sm"
                  leftIcon={<UserPlus className="w-4 h-4" />}
                >
                  Join
                </Button>
                <Button
                  onClick={openShareModal}
                  variant="glass"
                  size="sm"
                  leftIcon={<Share2 className="w-4 h-4" />}
                >
                  Invite
                </Button>
                <Button
                  onClick={handleCreateNewSession}
                  variant="ghost"
                  size="sm"
                  aria-label="New session"
                  title="New session"
                >
                  <Plus className="w-4 h-4" />
                </Button>
                <Button
                  onClick={handleCopyInvite}
                  variant="glass"
                  size="sm"
                  leftIcon={copied ? <Check className="w-4 h-4 text-success-400" /> : <Copy className="w-4 h-4" />}
                  title="Copy invite code"
                >
                  {session.inviteCode}
                </Button>
                <Button onClick={openSettings} variant="glass" size="sm" aria-label="Settings">
                  <Settings className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mx-4 mt-4 p-3 bg-error-500/20 border border-error-500/30 rounded-lg flex items-center justify-between">
              <span className="text-error-400 text-sm">{error}</span>
              <Button onClick={() => setError(null)} variant="ghost" size="sm" aria-label="Dismiss error">
                <X className="w-4 h-4 text-error-400" />
              </Button>
            </div>
          )}

          <div className="flex-1 p-4 lg:p-6 relative overflow-y-auto" ref={contentRef}>
            <CollabDocumentPanel
              annotations={annotations}
              onSelectionChange={setTextSelection}
              onAnnotationClick={setActiveAnnotation}
              onDocumentChange={setSharedDocument}
            />

            {textSelection && (
              <div className="sticky bottom-4 z-20 flex justify-center pointer-events-none mt-4">
                <div className="pointer-events-auto flex flex-wrap items-center gap-2 px-3 py-2 rounded-xl bg-khiimori-bg-elevated border border-khiimori-border/20 shadow-xl">
                  <span className="text-khiimori-text-muted text-xs max-w-[180px] truncate hidden sm:inline">
                    &ldquo;{textSelection.selectedText.slice(0, 60)}{textSelection.selectedText.length > 60 ? '…' : ''}&rdquo;
                  </span>
                  <Button onClick={() => handleAddAnnotation('highlight')} variant="glass" size="sm" leftIcon={<Highlighter className="w-4 h-4" />}>
                    Highlight
                  </Button>
                  <Button onClick={() => handleAddAnnotation('comment')} variant="glass" size="sm" leftIcon={<MessageSquare className="w-4 h-4" />}>
                    Comment
                  </Button>
                  <Button onClick={() => handleAddAnnotation('bookmark')} variant="glass" size="sm" leftIcon={<Bookmark className="w-4 h-4" />}>
                    Bookmark
                  </Button>
                  <Button onClick={() => handleAddAnnotation('tag')} variant="glass" size="sm" leftIcon={<Tag className="w-4 h-4" />}>
                    Tag
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border-t border-khiimori-border/10 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 max-w-4xl mx-auto">
              <p className="text-khiimori-text-muted text-sm">
                {textSelection
                  ? 'Use the toolbar above your selection, or the buttons below.'
                  : 'Select text in the shared page to highlight, comment, bookmark, or tag.'}
              </p>
              <span className="text-khiimori-text-muted text-sm">
                {activeAnnotations.length} active annotation{activeAnnotations.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 max-w-4xl mx-auto mt-3">
              <Button onClick={() => handleAddAnnotation('highlight')} variant="glass" size="sm" leftIcon={<Highlighter className="w-4 h-4" />}>
                Highlight
              </Button>
              <Button onClick={() => handleAddAnnotation('comment')} variant="glass" size="sm" leftIcon={<MessageSquare className="w-4 h-4" />}>
                Comment
              </Button>
              <Button onClick={() => handleAddAnnotation('bookmark')} variant="glass" size="sm" leftIcon={<Bookmark className="w-4 h-4" />}>
                Bookmark
              </Button>
              <Button onClick={() => handleAddAnnotation('tag')} variant="glass" size="sm" leftIcon={<Tag className="w-4 h-4" />}>
                Tag
              </Button>
            </div>
          </div>
        </div>

        {/* Right sidebar */}
        <div className={`w-full lg:w-80 lg:shrink-0 bg-khiimori-bg-elevated/5 backdrop-blur-xl border-t lg:border-t-0 lg:border-l border-khiimori-border/10 flex flex-col ${showParticipants ? '' : 'opacity-80'}`}>
          <div className="p-4 border-b border-khiimori-border/10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-khiimori-text-primary font-semibold">Participants</h3>
              <Button
                onClick={() => setShowParticipants(!showParticipants)}
                variant="ghost"
                size="sm"
                aria-label="Toggle participants"
              >
                <MoreHorizontal className="w-5 h-5 text-khiimori-text-muted" />
              </Button>
            </div>
            <div className="space-y-3">
              {session.participants.map((participant) => (
                <div key={participant.id} className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                      {(participant.displayName[0] ?? '?').toUpperCase()}
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-khiimori-border/5 ${participant.isActive ? 'bg-success-500' : 'bg-khiimori-bg-tertiary'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-khiimori-text-primary font-medium truncate">{participant.displayName}</div>
                    <div className="text-khiimori-text-muted text-sm capitalize">{participant.activity}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs shrink-0 ${
                    participant.role === 'owner' ? 'bg-primary-500/30 text-primary-300' :
                    participant.role === 'editor' ? 'bg-info-500/30 text-info-300' :
                    'bg-khiimori-bg-elevated/5 text-khiimori-text-secondary'
                  }`}>
                    {participant.role}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-khiimori-border/10">
              <Button onClick={openShareModal} variant="glass" size="sm" className="w-full" leftIcon={<Share2 className="w-4 h-4" />}>
                Invite others
              </Button>
              <Button onClick={openJoinModal} variant="ghost" size="sm" className="w-full" leftIcon={<UserPlus className="w-4 h-4" />}>
                Join with code
              </Button>
            </div>
          </div>

          <div className="flex-1 flex flex-col min-h-[200px]">
            <div className="p-4 border-b border-khiimori-border/10">
              <h3 className="text-khiimori-text-primary font-semibold flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Chat
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-64 lg:max-h-none">
              {comments.map((comment) => (
                <motion.div
                  key={comment.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-1"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-khiimori-text-primary font-medium text-sm">{comment.displayName}</span>
                    <span className="text-khiimori-text-muted text-xs">{formatCommentTime(comment.timestamp)}</span>
                  </div>
                  <p className="text-khiimori-text-secondary text-sm">{comment.content}</p>
                </motion.div>
              ))}
              {comments.length === 0 && (
                <p className="text-khiimori-text-muted text-center text-sm py-4">No messages yet</p>
              )}
            </div>
            <div className="p-4 border-t border-khiimori-border/10">
              <div className="flex items-center gap-2">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendComment()}
                  placeholder="Type a message..."
                />
                <Button
                  onClick={handleSendComment}
                  disabled={!newComment.trim()}
                  variant="primary"
                  size="md"
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-khiimori-border/10">
            <h3 className="text-khiimori-text-primary font-semibold mb-3">Annotations</h3>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {activeAnnotations.map((annotation) => {
                const quote = getSelectedQuote(annotation);
                const note = getAnnotationNote(annotation);
                const pos = getAnnotationPosition(annotation);
                return (
                  <button
                    key={annotation.id}
                    type="button"
                    onClick={() => setActiveAnnotation(annotation.id)}
                    className="w-full text-left p-3 bg-khiimori-bg-elevated/5 rounded-lg cursor-pointer hover:bg-khiimori-bg-elevated/10 transition-all border border-transparent hover:border-khiimori-border/10"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: annotation.color }} />
                      <span className="text-khiimori-text-primary text-sm font-medium truncate">{annotation.displayName}</span>
                      <span className="text-khiimori-text-muted text-xs capitalize ml-auto shrink-0">{annotation.type}</span>
                    </div>
                    {quote && (
                      <p className="text-khiimori-text-secondary text-xs italic line-clamp-2 mb-1">
                        &ldquo;{quote}&rdquo;
                      </p>
                    )}
                    {note && (
                      <p className="text-khiimori-text-muted text-xs line-clamp-2">{note}</p>
                    )}
                    {pos.tabTitle && (
                      <p className="text-khiimori-text-muted text-[10px] mt-1 truncate">on {pos.tabTitle}</p>
                    )}
                  </button>
                );
              })}
              {activeAnnotations.length === 0 && (
                <p className="text-khiimori-text-muted text-sm text-center py-2">No active annotations</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={!!activeAnnotation}
        onClose={() => setActiveAnnotation(null)}
        title={(() => {
          const a = annotations.find((x) => x.id === activeAnnotation);
          return a ? `${a.type.charAt(0).toUpperCase() + a.type.slice(1)} Annotation` : '';
        })()}
        description={(() => {
          const a = annotations.find((x) => x.id === activeAnnotation);
          return a ? `by ${a.displayName}` : '';
        })()}
        size="md"
      >
        {(() => {
          const annotation = annotations.find((a) => a.id === activeAnnotation);
          if (!annotation) return null;
          const quote = getSelectedQuote(annotation);
          const note = getAnnotationNote(annotation);
          const pos = getAnnotationPosition(annotation);
          return (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${annotation.color}30` }}>
                  <div style={{ color: annotation.color }}>{getAnnotationIcon(annotation.type)}</div>
                </div>
                <div>
                  <p className="text-khiimori-text-primary font-medium capitalize">{annotation.type}</p>
                  <p className="text-khiimori-text-muted text-sm">by {annotation.displayName}</p>
                </div>
              </div>
              {quote && (
                <div className="mb-4 p-3 rounded-lg bg-khiimori-bg-elevated/10 border-l-4" style={{ borderColor: annotation.color }}>
                  <p className="text-xs text-khiimori-text-muted uppercase tracking-wide mb-1">Selected text</p>
                  <p className="text-khiimori-text-secondary text-sm italic">&ldquo;{quote}&rdquo;</p>
                </div>
              )}
              {note && (
                <div className="mb-4">
                  <p className="text-xs text-khiimori-text-muted uppercase tracking-wide mb-1">
                    {annotation.type === 'comment' ? 'Comment' : annotation.type === 'tag' ? 'Tag' : 'Note'}
                  </p>
                  <p className="text-khiimori-text-secondary text-sm">{note}</p>
                </div>
              )}
              {(pos.tabTitle || pos.tabUrl) && (
                <div className="mb-4 text-xs text-khiimori-text-muted">
                  <span className="uppercase tracking-wide">Source: </span>
                  {pos.tabUrl ? (
                    <a href={pos.tabUrl} target="_blank" rel="noopener noreferrer" className="text-primary-300 hover:underline">
                      {pos.tabTitle || pos.tabUrl}
                    </a>
                  ) : (
                    pos.tabTitle
                  )}
                </div>
              )}
              <div className="flex gap-3">
                {!annotation.isResolved && (
                  <Button
                    onClick={() => {
                      handleResolveAnnotation(annotation.id);
                      setActiveAnnotation(null);
                    }}
                    variant="primary"
                    className="flex-1"
                  >
                    Resolve
                  </Button>
                )}
                <Button onClick={() => setActiveAnnotation(null)} variant="glass" className="flex-1">
                  Close
                </Button>
              </div>
            </>
          );
        })()}
      </Modal>

      <Modal
        isOpen={!!pendingAnnotation}
        onClose={() => {
          setPendingAnnotation(null);
          setAnnotationNote('');
        }}
        title={
          pendingAnnotation
            ? `${pendingAnnotation.type.charAt(0).toUpperCase() + pendingAnnotation.type.slice(1)} on selection`
            : ''
        }
        description="Add a note — everyone in the session will see the quoted text and your message."
        size="md"
      >
        {pendingAnnotation && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-khiimori-bg-elevated/10 border border-khiimori-border/10">
              <p className="text-xs text-khiimori-text-muted uppercase tracking-wide mb-1">Selected text</p>
              <p className="text-khiimori-text-secondary text-sm italic">
                &ldquo;{pendingAnnotation.selection.selectedText}&rdquo;
              </p>
            </div>
            <div>
              <label className="block text-sm text-khiimori-text-muted mb-1">
                {pendingAnnotation.type === 'comment'
                  ? 'Your comment'
                  : pendingAnnotation.type === 'tag'
                    ? 'Tag label'
                    : 'Bookmark note (optional)'}
              </label>
              <Input
                value={annotationNote}
                onChange={(e) => setAnnotationNote(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleConfirmAnnotation()}
                placeholder={
                  pendingAnnotation.type === 'comment'
                    ? 'What should the team know about this passage?'
                    : pendingAnnotation.type === 'tag'
                      ? 'e.g. needs-review, important'
                      : 'Why is this worth saving?'
                }
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleConfirmAnnotation}
                variant="primary"
                className="flex-1"
                isLoading={isSavingAnnotation}
                disabled={pendingAnnotation.type === 'comment' && !annotationNote.trim()}
              >
                Save {pendingAnnotation.type}
              </Button>
              <Button
                onClick={() => {
                  setPendingAnnotation(null);
                  setAnnotationNote('');
                }}
                variant="glass"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        title="Invite & Join"
        description="Share your session or join another with an invite code"
        size="md"
      >
        <div className="space-y-6">
          <div className="flex gap-2 p-1 bg-khiimori-bg-elevated/10 rounded-lg">
            <button
              type="button"
              onClick={() => setJoinModalTab('share')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                joinModalTab === 'share'
                  ? 'bg-primary-500/20 text-primary-300'
                  : 'text-khiimori-text-muted hover:text-khiimori-text-primary'
              }`}
            >
              Invite others
            </button>
            <button
              type="button"
              onClick={() => setJoinModalTab('join')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                joinModalTab === 'join'
                  ? 'bg-primary-500/20 text-primary-300'
                  : 'text-khiimori-text-muted hover:text-khiimori-text-primary'
              }`}
            >
              Join session
            </button>
          </div>

          {joinModalTab === 'share' && session && (
            <div className="space-y-4">
              <p className="text-khiimori-text-muted text-sm">
                Share this code or link so others can join <strong className="text-khiimori-text-primary">{session.name}</strong>.
              </p>
              <div className="p-4 bg-khiimori-bg-elevated/10 rounded-lg text-center">
                <p className="text-khiimori-text-muted text-xs uppercase tracking-wide mb-1">Invite code</p>
                <p className="text-2xl font-mono font-bold text-khiimori-text-primary tracking-widest">{session.inviteCode}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCopyInvite} variant="glass" className="flex-1" leftIcon={copied ? <Check className="w-4 h-4 text-success-400" /> : <Copy className="w-4 h-4" />}>
                  {copied ? 'Copied!' : 'Copy code'}
                </Button>
                <Button onClick={handleCopyInviteLink} variant="primary" className="flex-1" leftIcon={copiedLink ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}>
                  {copiedLink ? 'Link copied!' : 'Copy link'}
                </Button>
              </div>
              <p className="text-khiimori-text-muted text-xs break-all">
                {buildInviteLink(session.inviteCode)}
              </p>
            </div>
          )}

          {joinModalTab === 'join' && (
            <div className="space-y-4">
              <p className="text-khiimori-text-muted text-sm">
                Enter an invite code from another session to switch to it.
              </p>
              {error && (
                <div className="p-3 bg-error-500/20 border border-error-500/30 rounded-lg text-error-400 text-sm">
                  {error}
                </div>
              )}
              <Input
                value={inviteCodeInput}
                onChange={(e) => setInviteCodeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJoinSession()}
                placeholder="Enter invite code"
                autoFocus
              />
              <Button
                onClick={handleJoinSession}
                disabled={!inviteCodeInput.trim() || isJoining}
                variant="primary"
                className="w-full"
                isLoading={isJoining}
                leftIcon={<UserPlus className="w-4 h-4" />}
              >
                Join session
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        title="Session Settings"
        description="Manage your collaborative session"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-khiimori-text-muted mb-1">Session name</label>
            <Input value={settingsName} onChange={(e) => setSettingsName(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm text-khiimori-text-muted mb-1">Max participants</label>
            <Input
              type="number"
              min={2}
              max={50}
              value={settingsMaxParticipants}
              onChange={(e) => setSettingsMaxParticipants(Number(e.target.value) || 10)}
            />
          </div>
          <div className="p-3 bg-khiimori-bg-elevated/10 rounded-lg">
            <p className="text-khiimori-text-muted text-sm">Invite code</p>
            <p className="text-khiimori-text-primary font-mono text-lg">{session.inviteCode}</p>
          </div>
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSaveSettings} variant="primary" className="flex-1" isLoading={isSavingSettings}>
              Save
            </Button>
            <Button onClick={() => setShowSettings(false)} variant="glass" className="flex-1">
              Cancel
            </Button>
          </div>
          <Button
            onClick={handleDeleteSession}
            variant="ghost"
            className="w-full text-error-400 hover:text-error-300"
            leftIcon={<Trash2 className="w-4 h-4" />}
          >
            Delete session
          </Button>
        </div>
      </Modal>
    </PageLayout>
  );
}
